/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCookie } from '@/hooks/useCookie'
import { useFormik } from 'formik'
import { useEffect, useRef, useState } from 'react'

import ButtonLink from '@/components/ButtonLink'
import {
  FormButton,
  FormGroup,
  FormInput,
  FormText,
  FormTextRequire,
  FormView,
} from '@/components/Form'
import { FormCheckBoxSingle } from '@/components/Form/FormCheckBoxSingle'
import ImageOptimize from '@/components/ImageOptimize'
import { RegisterSlider } from '@/components/RegisterSlider'
import { SideBarBanner } from '@/components/SideBarBanner'
import { BannerType, DEFAULT_AREA, Languages } from '@/constants'
import { useNotification } from '@/containers'
import HeaderNoMenu from '@/layouts/HeaderNoMenu'
import type { BannerList } from '@/types'
import { api } from '@/utils'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import { getBanner, getConfig } from '@/utils/endpoint'
import { translateSchema } from '@/utils/validations'
import clsx from 'clsx'
import { getCookie, getCookies } from 'cookies-next'
import { isEmpty, omitBy, pick } from 'lodash'
import omit from 'lodash/omit'
import { type GetServerSideProps } from 'next'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

type RegisterProps = {
  banner: BannerList[]
}

const Register = ({ banner }: RegisterProps) => {
  const t = useTranslations('Register')
  const tOtp = useTranslations('Login')
  const { systemConfig } = useNotification()
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [requestId, setRequestId] = useCookie('requestId', {})
  const [isVerifyOtp, setIsVerifyOtp] = useState(false)
  const [, setEmailVerify] = useCookie('emailVerify', {})

  const [isSuccess, setIsSuccess] = useState(false)
  const [remainTime, setRemainTime] = useState(5)
  const timeRef = useRef<any>(null)
  const onSubmitFn = () => {
    setIsSubmitted(true)
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
      email: '',
      preference: false,
    },
    validationSchema: translateSchema('register', t),

    onSubmit: async (values: {
      name: string
      phone: string
      email: string
      preference: boolean
    }) => {
      const captchaKey = process.env.NEXT_PUBLIC_CAPTCHA_KEY as string
      let tokenReCaptcha: string | undefined

      if (captchaKey && window.grecaptcha) {
        try {
          tokenReCaptcha = await window.grecaptcha.execute(captchaKey, {
            action: 'register',
          })
        } catch (e) {
          console.warn('reCAPTCHA execute failed, proceeding without token', e)
        }
      } else if (captchaKey) {
        console.warn('reCAPTCHA key configured but script not yet loaded')
      }

      setErrorMessage('')
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_AUTH_API_URL}/api/auth/register`,
        {
          method: 'POST',
          body: JSON.stringify(omit(omitBy(values, isEmpty), 'preference')),
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': getCookie('language') || Languages.VN,
            'Area-Code': getCookie('Area-Code') || DEFAULT_AREA,
            ...(tokenReCaptcha ? { recaptcha: tokenReCaptcha } : {}),
          } as any,
        }
      ).catch((e: Error) => {
        return e
      })
      if (res instanceof Error) {
        return res
      }
      const { ...data } = await res.json()

      if (!data.statusCode) {
        if (values.email) {
          let verificationTokenReCaptcha: string | undefined
          if (captchaKey && window.grecaptcha) {
            try {
              verificationTokenReCaptcha = await window.grecaptcha.execute(captchaKey, {
                action: 'resend_register_otp',
              })
            } catch (e) {
              console.warn('reCAPTCHA execute failed for otp, proceeding without token', e)
            }
          }

          const res = await api(`api/auth/registration/verification/otp`, {
            method: 'POST',
            baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
            headers: { ...(verificationTokenReCaptcha ? { recaptcha: verificationTokenReCaptcha } : {}) },
            data: pick(formik.values, ['name', 'phone', 'password', 'email']),
          })

          if (res?.message) {
            setErrorMessage(res.message)
            setIsProcessing(false)
            return
          }
          setEmailVerify(values.email)
          setIsVerifyOtp(true)
          setRequestId(res.data.requestId)
          setIsProcessing(false)
          return
        }
        setIsSuccess(true)
        const { requestId } = data.data
        setRequestId(requestId)
        setIsProcessing(false)
      }

      if (data.validators) {
        const errors = data.validators
          .reduce((acc: any, cur: any) => {
            const { ...identifier } = cur.constraints
            return [
              ...acc,
              {
                key: cur.property,
                message: Object.values(identifier)[0],
              },
            ]
          }, [])
          .reduce(
            (obj: any, item: any) =>
              Object.assign(obj, { [item.key]: item.message }),
            {}
          )
        setIsProcessing(false)
        formik.setErrors(errors)
        return
      }

      setErrorMessage(data.message)
      setIsProcessing(false)
      setTimeout(() => {
        setErrorMessage('')
      }, 5000)
    },
  })

  const handleResendOtp = async () => {
    setErrorMessage('')
    const captchaKey = process.env.NEXT_PUBLIC_CAPTCHA_KEY as string
    let tokenReCaptcha: string | undefined

    if (captchaKey && window.grecaptcha) {
      try {
        tokenReCaptcha = await window.grecaptcha.execute(captchaKey, {
          action: 'resend_register_otp',
        })
      } catch (e) {
        console.warn('reCAPTCHA execute failed, proceeding without token', e)
      }
    } else if (captchaKey) {
      console.warn('reCAPTCHA key configured but script not yet loaded')
    }
    api(`api/auth/registration/verification/otp`, {
      method: 'POST',
      baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
      headers: { ...(tokenReCaptcha ? { recaptcha: tokenReCaptcha } : {}) },
      data: pick(formik.values, ['name', 'phone', 'password', 'email']),
    })
      .then(res => {
        setRequestId(res.data.requestId)
        clearInterval(timeRef.current)
        setRemainTime(120)
        timeRef.current = setInterval(() => {
          setRemainTime(prevTime => prevTime - 1)
        }, 1000)
      })
      .catch(error => {
        setErrorMessage(error.message)
      })
  }

  const formikOtp = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: translateSchema('otp', tOtp),
    onSubmit: async values => {
      setIsProcessing(true)
      const res = await api(`api/auth/registration/verification`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
        data: {
          code: values.otp.trim(),
          requestId,
        },
      })
      if (res instanceof Error) {
        setErrorMessage(res.message)
        setIsProcessing(false)
        return
      }
      setIsProcessing(false)
      setIsSuccess(true)
    },
  })
  useEffect(() => {
    timeRef.current = setInterval(() => {
      setRemainTime(prev => prev - 1)
    }, 1000)
    return () => {
      clearInterval(timeRef.current)
    }
  }, [])

  useEffect(() => {
    if (remainTime === 0) {
      clearInterval(timeRef.current)
    }
  }, [remainTime])

  return (
    <>
      <style global jsx>{`
        body::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <HeaderNoMenu />
      <main>
        <SideBarBanner className='top-[37%]'></SideBarBanner>
        <div
          className={`h-[calc(100vh - 129px)] m-auto flex max-w-[1200px] items-center pt-[5px] ${
            isSuccess ? 'mb-[60px] justify-center' : 'gap-x-[210px]'
          }`}
        >
          {isSuccess ? (
            <>
              <div className='mt-[60px]'>
                <ImageOptimize
                  src={'/register.png'}
                  alt='đăng ký'
                  width={598}
                  height={400}
                />
                <div className=' mt-[40px] text-center'>
                  <h3 className=' text-titleSection text-dark-400'>
                    {t('registerSuccess')}
                  </h3>
                  <p className='mt-2 text-fieldInput text-dark-400'>
                    {t('thankyou', { brand: systemConfig?.websiteBrandName })}
                  </p>
                  <ButtonLink
                    href='/login'
                    text={t('login')}
                    classes='text-white text-fieldInput font-bold uppercase'
                    className='mt-[40px] rounded bg-primary-second text-mainBody'
                  ></ButtonLink>
                </div>
              </div>
            </>
          ) : (
            <>
              {isVerifyOtp ? (
                <div className='flex w-full flex-2 flex-col py-[60px]'>
                  <div>
                    <FormView
                      onSubmit={formikOtp.handleSubmit}
                      buttonClassName='mt-4'
                    >
                      <FormGroup className='flex w-full flex-col'>
                        <FormText className='text-grey-800 mb-[32px] text-left text-Headline font-normal'>
                          <h1 className='text-HeadlineLarge text-center font-bold text-dark-400'>
                            Xác thực OTP
                          </h1>
                          <p className='mt-[12px] text-center text-Headline text-dark-400'>
                            Mã OTP đã được gửi qua email bạn đăng ký
                          </p>
                        </FormText>
                        <FormInput
                          name='otp'
                          placeholder={'Nhập mã OTP'}
                          value={formikOtp.values.otp.trim()}
                          isError={isSubmitted && formikOtp.errors.otp}
                          onChange={e => {
                            formikOtp.handleChange({
                              target: {
                                value: e.target.value.trim(),
                                name: 'otp',
                              },
                            })
                            setErrorMessage('')
                          }}
                          alert
                          className={clsx(
                            formikOtp.errors.otp && 'border border-red-600',
                            'placeholder:text-grey-900 !h-12 w-[380px] min-w-[360px] rounded-[6px] border border-solid border-dark-200 text-left  transition hover:cursor-pointer'
                          )}
                        ></FormInput>
                        {isSubmitted && formikOtp.errors.otp ? (
                          <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-red-500'>
                            {formikOtp.errors.otp}
                          </FormTextRequire>
                        ) : (
                          ''
                        )}
                        {errorMessage && isSubmitted ? (
                          <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-red-500'>
                            {errorMessage}
                          </FormTextRequire>
                        ) : (
                          <></>
                        )}
                      </FormGroup>
                      <FormButton
                        isProcessing={isProcessing}
                        className={clsx(
                          formikOtp.dirty &&
                            ' !bg-product-salePrice text-Headline  !text-white-400',
                          'disabled:text-[rgb(0 0 0 / 25%)] disabled:bg-grey h-12 w-full rounded-[6px] border bg-dark/20 p-[10px] font-semibold uppercase text-black-400/25 disabled:text-black-400/25 disabled:outline-none'
                        )}
                        onClick={onSubmitFn}
                        disabled={!formikOtp.dirty}
                      >
                        Xác thực
                      </FormButton>
                    </FormView>
                  </div>
                  {remainTime === 0 ? (
                    <div className='mb-[40px] mt-[32px] text-center  text-fieldLabel'>
                      <span className=' text-dark-400'>
                        Tôi không nhận được mã,
                      </span>{' '}
                      <span
                        className='ml-[3px] cursor-pointer text-fieldLabel text-blue'
                        onClick={handleResendOtp}
                      >
                        Gửi lại mã
                      </span>
                    </div>
                  ) : (
                    <div className='mb-[40px] mt-[32px] text-center'>
                      Nếu không nhận được mã, thử lại sau
                      <span className='ml-[2px]'>{remainTime} giây</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className='flex w-full flex-2 flex-col py-[60px]'>
                  <div className='mb-[40px] text-titleSection font-bold text-dark-400'>
                    {t('register')}
                  </div>
                  <div className=''>
                    <FormView onSubmit={formik.handleSubmit}>
                      <FormGroup className='mb-[20px] flex w-full flex-col'>
                        <FormText className='mb-1 text-left text-fieldInput font-bold'>
                          {t('fullname')}
                        </FormText>
                        <FormInput
                          name='name'
                          placeholder={t('fullname')}
                          value={formik.values.name}
                          isError={isSubmitted && formik.errors.name}
                          onChange={formik.handleChange}
                          alert
                          className={clsx(
                            isSubmitted &&
                              formik.errors.name &&
                              'border border-primary-second',
                            '  !h-12 w-[380px] min-w-[360px] rounded-lg border border-solid border-dark-200 text-left transition  placeholder:text-dark-400/70 hover:cursor-pointer'
                          )}
                        ></FormInput>
                        {isSubmitted && formik.errors.name ? (
                          <FormTextRequire className=' text-fieldInput text-sm font-normal text-primary-second'>
                            {formik.errors.name}
                          </FormTextRequire>
                        ) : (
                          ''
                        )}
                      </FormGroup>
                      <FormGroup className='mb-[20px] flex w-full flex-col'>
                        <FormText className='mb-1 text-left text-fieldInput font-bold'>
                          {t('phone')}
                        </FormText>
                        <FormInput
                          name='phone'
                          placeholder={t('phonePld')}
                          value={formik.values.phone}
                          isError={isSubmitted && formik.errors.phone}
                          onChange={e => {
                            formik.handleChange({
                              target: {
                                value: e.target.value.trim().replace(/\D/, ''),
                                name: 'phone',
                              },
                            })
                            setErrorMessage('')
                          }}
                          alert
                          className={clsx(
                            isSubmitted &&
                              formik.errors.phone &&
                              'border border-primary-second',
                            '  !h-12 w-[380px] min-w-[360px] rounded-lg border border-solid border-dark-200 text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                          )}
                        />
                        {isSubmitted && formik.errors.phone ? (
                          <FormTextRequire className=' text-fieldInput text-sm font-normal text-primary-second'>
                            {formik.errors.phone}
                          </FormTextRequire>
                        ) : (
                          ''
                        )}
                      </FormGroup>
                      <FormGroup className='mb-[20px] flex w-full flex-col'>
                        <FormText className='mb-[4px] block text-left text-fieldInput font-bold'>
                          Email
                        </FormText>
                        <FormInput
                          name='email'
                          type='email'
                          placeholder={t('emailPld')}
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          alert
                          className={`${
                            (isSubmitted && formik.errors.email) ||
                            (formik.touched['email'] && formik.errors.email)
                              ? 'border !border-red-600'
                              : ''
                          } disabled:bg-grey-200 rounded border border-solid border-dark-200 text-left text-fieldInput outline-none transition placeholder:text-dark-400/70 hover:cursor-pointer hover:border-blue focus:border-blue disabled:cursor-not-allowed disabled:text-dark-400/50`}
                        ></FormInput>
                      </FormGroup>

                      <FormGroup className='mb-8'>
                        <FormCheckBoxSingle
                          onChange={formik.handleChange}
                          name='preference'
                          checked={formik.values.preference}
                        >
                          <div className='flex-1 cursor-pointer text-dark-400'>
                            {t('agreeWith')}{' '}
                            <Link
                              className='relative text-blue'
                               href={getTranslate(systemConfig?.pagePolicy) || ''}
                              target='blank'
                            >
                              {t('termsAndConditions')}
                            </Link>{' '}
                            {t('member', {
                              brand: systemConfig?.websiteBrandName,
                            })}
                          </div>
                        </FormCheckBoxSingle>
                        {isSubmitted && formik.errors.preference ? (
                          <FormTextRequire className=' text-base text-fieldInput font-normal text-primary-second'>
                            {formik.errors.preference}
                          </FormTextRequire>
                        ) : (
                          ''
                        )}
                      </FormGroup>
                      <FormButton
                        className={clsx(
                          formik.dirty &&
                            ' !bg-primary-base text-fieldInput  !text-white',
                          ' h-[48px] w-full rounded border bg-dark/30 p-[10px] font-bold uppercase text-black-400/25 disabled:text-black-400/25 disabled:outline-none'
                        )}
                        disabled={isProcessing}
                        isProcessing={isProcessing}
                        onClick={onSubmitFn}
                      >
                        {t('register')}
                      </FormButton>
                    </FormView>
                  </div>
                  <span className='mt-[32px] text-center'>
                    {t('justKnow', { brand: systemConfig?.websiteBrandName })}{' '}
                    <Link
                      className='text-fieldInput font-normal text-blue-400'
                      href='/login'
                    >
                      {t('loginNow')}
                    </Link>
                  </span>
                </div>
              )}
              <div className='relative -mt-4 min-w-[538px]'>
                <RegisterSlider
                  sliders={
                    banner.find(
                      (e: BannerList) => e.type === BannerType.REGISTER
                    )?.items
                  }
                ></RegisterSlider>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  )
}

export default Register

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale,
}) => {
  const { language, messages } = await setContext(getCookies({ req, res }), {
    language: locale,
  })
  const [banner, systemConfig] = await Promise.all([getBanner(), getConfig()])
  return {
    props: {
      messages,
      language,
      banner,
      systemConfig,
    },
  }
}
